import { S25ItemI } from "../../../../pojo/S25ItemI";
import { Item } from "../../../../pojo/Item";
import { Proto } from "../../../../pojo/Proto";

import NumericalBoolean = Proto.NumericalBoolean;

export type DurationNameMap = {
    D: "Day";
    W: "Week";
    M: "Month";
    Y: "Year";
};

export const durationNameMap: DurationNameMap = { D: "Day", W: "Week", M: "Month", Y: "Year" };

export const NAME_MAX_LENGTH = 100;
export const DURATION_TYPE_MAX_LENGTH = 10;
export const EDITOR_DURATION_TYPES = ["D", "W", "M", "Y"] as const;

export const MAX_ROLLING_DURATION_BY_UNIT = {
    D: 500,
    W: 52,
    M: 24,
    Y: 5,
} as const;

export const MAX_FIXED_WINDOW_DAYS = 999;

export const windowModeNameMap = {
    rolling: "Rolling period",
    future: "Future only (rolling)",
    fixed: "Fixed date range",
    today: "Forward from today",
} as const;

export type WindowMode = keyof typeof windowModeNameMap;

export const DURATION_WINDOW_MODES: WindowMode[] = ["rolling", "future"];
export const FIXED_WINDOW_MODES: WindowMode[] = ["fixed", "today"];
export type WindowModeNameMap = typeof windowModeNameMap;

export const DEFAULT_WINDOW_MODE: WindowMode = "rolling";

export type OrgScopeMode = "list" | "search";

export type FormModel = {
    itemName: string;
    active: boolean;
    quotaTypeItemId: string;
    duration: number | null;
    durationType: string;
    windowMode: WindowMode;
    quotaStartDt: Date | null;
    quotaEndDt: Date | null;
    quantity: number | null;
    maxTimeHours: number | null;
    maxTimeMinutes: number | null;
    skipExisting: boolean;
    countByEvent: boolean;
    includeRequests: boolean;
    groupItems: boolean;
    orgScopeMode: OrgScopeMode;
    organizations: S25ItemI[];
    organizationSearch: QuotaScopeItem | null;
    locations: S25ItemI[];
    eventTypes: S25ItemI[];
    secGroups: S25ItemI[];
    comment: string;
    limitToTimeOfDay: boolean;
    dow: string;
    daySelection: boolean[];
    allowedTimeStart: string | null;
    allowedTimeEnd: string | null;
};

export type SelectionBaseline = {
    orgScopeMode: OrgScopeMode;
    organizations: S25ItemI[];
    organizationSearch: QuotaScopeItem | null;
    locations: S25ItemI[];
    eventTypes: S25ItemI[];
    secGroups: S25ItemI[];
};

export interface RawData {
    quota_id: number;
    quota_name: string;
    comment?: string;
    active: boolean;
    quota_type: number;
    quantity: number;
    duration: number;
    max_minutes: number;
    duration_type: keyof DurationNameMap;
    group_objects: number;
    skip_existing: number;
    count_by_event?: number;
    include_requests?: number;
    window_mode?: WindowMode;
    quota_start_dt?: string;
    quota_end_dt?: string;
    dow?: string;
    allowed_time_start?: string;
    allowed_time_end?: string;
    itemIds?: S25ItemI[];
    organizations?: S25ItemI[];
    organizationSearches?: QuotaScopeItem[];
    locations?: S25ItemI[];
    eventTypes?: S25ItemI[];
    secGroups?: S25ItemI[];
    [key: string]: any;
}

export interface JsonOutput {
    _status: string;
    active?: boolean;
    quota_id?: number;
    quota_name?: string;
    comment?: string;
    quota_type: number;
    quantity: number;
    max_minutes?: number;
    duration?: number;
    duration_type?: string;
    group_objects: number;
    skip_existing: number;
    count_by_event?: number;
    include_requests?: number;
    window_mode?: WindowMode;
    quota_start_dt?: string;
    quota_end_dt?: string;
    dow?: string;
    allowed_time_start?: string;
    allowed_time_end?: string;
    organizations: S25ItemI[];
    organizationSearches: QuotaScopeItem[];
    locations: S25ItemI[];
    eventTypes: S25ItemI[];
    secGroups: Array<{ itemId: number; _status: string }>;
}

export interface Detailed {
    itemId?: number;
    itemName?: string;
    type?: S25ItemI;
    duration?: number;
    durationType?: string;
    durationTypeName?: string;
    useQuantity?: boolean;
    quantity?: number;
    maxTime?: string;
    groupItems?: boolean;
    skipExisting?: boolean;
    countByEvent?: boolean;
    includeRequests?: boolean;
    windowMode?: WindowMode;
    windowModeName?: string;
    quotaStartDt?: string;
    quotaEndDt?: string;
    dow?: string;
    allowedTimeStart?: string;
    allowedTimeEnd?: string;
    windowStartLabel?: string;
    windowEndLabel?: string;
    organizations?: S25ItemI[];
    organizationSearches?: QuotaScopeItem[];
    locations?: S25ItemI[];
    eventTypes?: S25ItemI[];
    securityGroups?: S25ItemI[];
    active?: boolean;
    comment?: string;
    status?: string;
    items?: S25ItemI[];
    secGroups?: S25ItemI[];
    addedLocations?: S25ItemI[];
    removedLocations?: S25ItemI[];
    addedEventTypes?: S25ItemI[];
    removedEventTypes?: S25ItemI[];
    addedOrgs?: S25ItemI[];
    removedOrgs?: S25ItemI[];
    addedOrgSearches?: QuotaScopeItem[];
    removedOrgSearches?: QuotaScopeItem[];
    addedGroups?: S25ItemI[];
    removedGroups?: S25ItemI[];
    [key: string]: any;
}

export interface Type {
    itemId: number;
    itemName: string;
}

export const enum TypeIds {
    System = 99,
    Location = 4,
    User = 3,
    Organization = 2,
}

export const enum ObjectTypeIds {
    Organization = 2,
    OrganizationSearch = 102,
    EventType = 19,
}

export type QuotaScopeItem = S25ItemI<Item.Ids | ObjectTypeIds>;

export interface CheckOccurrence {
    allowed: NumericalBoolean;
    allowed_time: NumericalBoolean;
    allowed_count: NumericalBoolean;
    uuid: string;
    rsrvId: number;
    startDt: Date;
    endDt: Date;
    object_id: number;
    object_type_id: number;
    location?: S25ItemI;
}

export interface CheckResult {
    quota: Detailed & {
        subjectTypeId: number;
        subjectTypeName: string;
    };
    occ: CheckOccurrence;
}

export interface LocationItem extends S25ItemI {
    occ_id?: string;
    itemTypeId: 4;
}

export type CheckResultMessageContext = {
    quota?: Detailed;
    occ?: CheckOccurrence;
    location?: S25ItemI;
};
